import {canReassignTasks} from '../../TaskManagement/taskTimerState';
import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Group from '../../entities/group';
import Role from '../../entities/role';
import Deadline from '../../entities/deadline';
import Task from '../../entities/task';
import TemplateTask from '../../entities/templateTask';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteTemplateTask, collectTemplateTaskDeleteImpact} from './templateTaskDeps';
export function canDeleteGroup(): boolean {
return canCascadeDelete();
}
export function collectGroupDeleteImpact(group: Group, context: DeleteImpactContext): void {
const key = 'group:' + group.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const roleGroupsImpact = workingRows<Role>('role', findAll<Role>('roles')).filter(roleRef => roleRef.groups?.includes(group.id));
roleGroupsImpact.forEach(roleRef => addDeleteImpact(context, 'role:' + roleRef.id, 'roles'));const deadlineAssignedToTypeImpact = workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineRef => deadlineRef.assignedToType === 'G' && deadlineRef.assignedTo === group.id && deadlineRef.status !== 'D');
deadlineAssignedToTypeImpact.forEach(deadline => addDeleteImpact(context, 'deadline:' + deadline.id, 'deadlines'));
const taskAssignedToTypeImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.assignedToType === 'G' && taskRef.assignedTo === group.id && taskRef.status !== 'D');
taskAssignedToTypeImpact.forEach(task => addDeleteImpact(context, 'task:' + task.id, 'tasks'));workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['G'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === group.id).forEach(templateTask => {
if (context.visited.has('templateTask:' + templateTask.id)) return;
addDeleteImpact(context, 'templateTask:' + templateTask.id, 'templateTasks');
collectTemplateTaskDeleteImpact(templateTask, context);
});
}
export function deleteImpactGroup(group: Group): DeleteImpact {
const context = deleteImpactContext();
collectGroupDeleteImpact(group, context);
return deleteImpactCounts(context);
}
export function deleteGroup(group: Group, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteGroup()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectGroupDeleteImpact(group, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || group.id};
}
const key = 'group:' + group.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['G'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === group.id).forEach(templateTask => {deleteTemplateTask(templateTask, mergeId, cascade);});
workingRows<Role>('role', [...store.getState().roles.getMappingFor('groups', group.id)]).filter(roleTemp => roleTemp.groups?.includes(group.id)).forEach(roleTemp => {
const _role = ((_work.get('role:' + roleTemp.id) ?? roleTemp) as Role).clone();
if (_role.status === 'D') return;
_role.status = 'U';
_role.addedOn = newHRTimestamp();
_role.addedBy = getCurrentUserId();
_role.importSource = targetImportSource;
_role.importId = mergeId;
_role.groups = _role.groups?.filter(id => id !== group.id) || [];

_work.set('role:' + _role.id, _role);
dispatch(Actions.RoleActions.saveRoleQueue(_role), 'role:' + _role.id);
});workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineTemp => deadlineTemp.assignedToType === 'G' && deadlineTemp.assignedTo === group.id && deadlineTemp.status !== 'D').forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.assignedTo = null;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
_work.set('deadline:' + _deadline.id, _deadline);
dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline), 'deadline:' + _deadline.id);
});
workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.assignedToType === 'G' && taskTemp.assignedTo === group.id && taskTemp.status !== 'D').filter(canReassignTasks).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.assignedTo = null;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
const deleted = group.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.GroupActions.removeGroupQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceGroup(group: Group, replaceWith: Group, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = group.id;}
const _work = workingSet();
workingRows<Role>('role', [...store.getState().roles.getMappingFor('groups', group.id)]).filter(roleTemp => roleTemp.groups?.includes(group.id)).forEach(roleTemp => {
const _role = ((_work.get('role:' + roleTemp.id) ?? roleTemp) as Role).clone();
if (_role.status === 'D') return;
_role.status = 'U';
_role.addedOn = newHRTimestamp();
_role.addedBy = getCurrentUserId();
_role.importSource = targetImportSource;
_role.importId = mergeId;
_role.groups = _role.groups?.map(id => (id === group.id) ? replaceWith.id : id) || [];

_work.set('role:' + _role.id, _role);
store.dispatch(Actions.RoleActions.saveRoleQueue(_role));
});
workingRows<Deadline>('deadline', findAll<Deadline>('deadlines')).filter(deadlineTemp => deadlineTemp.assignedToType === 'G' && deadlineTemp.assignedTo === group.id && deadlineTemp.status !== 'D').forEach(deadlineTemp => {
const _deadline = ((_work.get('deadline:' + deadlineTemp.id) ?? deadlineTemp) as Deadline).clone();
if (_deadline.status === 'D') return;
_deadline.assignedTo = replaceWith.id;
_deadline.status = 'U';
_deadline.addedOn = newHRTimestamp();
_deadline.addedBy = getCurrentUserId();
_deadline.importSource = targetImportSource;
_deadline.importId = mergeId;
_work.set('deadline:' + _deadline.id, _deadline);
store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
});
workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.assignedToType === 'G' && taskTemp.assignedTo === group.id && taskTemp.status !== 'D').filter(canReassignTasks).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.assignedTo = replaceWith.id;
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});


workingRows<TemplateTask>('templateTask', findAll<TemplateTask>('templateTasks')).filter(templateTask => templateTask.status !== 'D' && ['G'].includes(templateTask.assignedToType || '') && templateTask.assignedTo === group.id).forEach(templateTaskTemp => {
const _templateTask = ((_work.get('templateTask:' + templateTaskTemp.id) ?? templateTaskTemp) as TemplateTask).clone();
if (_templateTask.status === 'D') return;
_templateTask.assignedTo = replaceWith.id;
_templateTask.status = 'U';
_templateTask.addedOn = newHRTimestamp();
_templateTask.addedBy = getCurrentUserId();
_templateTask.importSource = targetImportSource;
_templateTask.importId = mergeId;
_work.set('templateTask:' + _templateTask.id, _templateTask);
store.dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask));
});
return group;
}
